/**
 * IndexTestPanel — Slide-out panel for testing a versioned index before promotion.
 * Routes queries to a specific (non-alias) index via the test-query endpoint.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

import {
  X, Send, Loader2, Bot, User, ArrowUpCircle, Trash2,
  Database, MessageSquare, FileText, ChevronDown, ChevronRight,
  Image as ImageIcon, RefreshCw, Check, Wand2, Pencil, EyeOff, Eye, Sparkles
} from 'lucide-react';
import type { ApprovalBatch } from './types';
import { API_BASE, authFetch, getAgentColor } from './constants';
import StreamingText from '../../components/StreamingText';

interface IndexTestPanelProps {
  batch: ApprovalBatch;
  onClose: () => void;
  onPromote: (batch: ApprovalBatch) => void;
  onReject: (batch: ApprovalBatch) => void;
}

interface TestMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: Array<{ title: string; content: string; url?: string }>;
  latencyMs?: number;
  agentName?: string;
}

interface ReviewImage {
  docId: string;
  docName: string;
  docUrl: string;
  pageNumber: number;
  sectionTitle: string;
  imageIndex: number;
  imageUrl: string;
  caption: string;
  // AA-220 follow-up ("hide generic UI icons"): `hidden` is the CURRENT
  // effective state — excluded from citations/inline markdown at chat time
  // (kb_concierge.py) — while `suggestedHidden` is the Vision classifier's
  // original recommendation, kept around so "accept suggested" / "revert to
  // AI suggestion" have something to act on independent of manual overrides.
  hidden: boolean;
  suggestedHidden: boolean;
}

const imageKey = (img: { docId: string; imageIndex: number }) => `${img.docId}::${img.imageIndex}`;

const SAMPLE_QUERIES: Record<string, string[]> = {
  'hrpolicy': ['What is the PTO policy?', 'How do I request FMLA leave?', 'What are the dress code requirements?'],
  'iris': ['How do I process a customer complaint?', 'What is the route delivery procedure?', 'How do I handle a damaged garment?'],
  'product': ['Show me flame resistant uniforms', 'What PPE options are available?', 'Do you have high-visibility vests?'],
  'pwms-support': ['How do I log into PWMS?', 'How to create a work order?', 'What is the barcode scanning process?'],
  'direct-embroidery': ['What thread colors are available?', 'How do I set up a new embroidery design?', 'What is the maximum stitch count?'],
  'unifirst-docs': ['How do I access UniFirst Docs?', 'What training materials are available?', 'How to submit a help request?'],
  'orientation': ['What is the first day schedule?', 'Where do I find my benefits enrollment?', 'What safety training is required?'],
  'service-desk': ['How do I reset my password?', 'How to submit an IT ticket?', 'What is the VPN setup process?'],
};

const IndexTestPanel: React.FC<IndexTestPanelProps> = ({
  batch, onClose, onPromote, onReject
}) => {
  const [messages, setMessages] = useState<TestMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Citation source snippets are collapsed by default (per message index) since most
  // test questions don't need them — expand to verify caption/image quality per AA-220.
  const [expandedCitations, setExpandedCitations] = useState<Set<number>>(new Set());
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // "Review Images" tab (AA-220 follow-up) — lets an admin sanity-check
  // every image extracted into this not-yet-promoted index, edit a caption
  // by hand, or bulk-select images to re-run through GPT-4o Vision with
  // optional guidance before promoting.
  const [activeTab, setActiveTab] = useState<'chat' | 'images'>('chat');
  const [images, setImages] = useState<ReviewImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState<string | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [guidance, setGuidance] = useState('');
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [recaptioning, setRecaptioning] = useState(false);

  const toggleCitations = (messageIndex: number) => {
    setExpandedCitations(prev => {
      const next = new Set(prev);
      if (next.has(messageIndex)) next.delete(messageIndex);
      else next.add(messageIndex);
      return next;
    });
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadImages = useCallback(async () => {
    setImagesLoading(true);
    setImagesError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/indexes/${encodeURIComponent(batch.indexName || '')}/images?environment=${batch.environment}`
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load images');
      setImages(data.images || []);
      setImagesLoaded(true);
    } catch (err: any) {
      setImagesError(err.message || 'Failed to load images');
    } finally {
      setImagesLoading(false);
    }
  }, [batch.indexName, batch.environment]);

  useEffect(() => {
    if (activeTab === 'images' && !imagesLoaded && !imagesLoading) {
      loadImages();
    }
  }, [activeTab, imagesLoaded, imagesLoading, loadImages]);

  const toggleSelected = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(prev => (prev.size === images.length ? new Set() : new Set(images.map(imageKey))));
  };

  const draftFor = (img: ReviewImage) => drafts[imageKey(img)] ?? img.caption;
  const isDirty = (img: ReviewImage) => imageKey(img) in drafts && drafts[imageKey(img)] !== img.caption;

  const applyResult = (img: ReviewImage, newCaption: string) => {
    setImages(prev => prev.map(i => (i === img ? { ...i, caption: newCaption } : i)));
    setDrafts(prev => {
      const next = { ...prev };
      delete next[imageKey(img)];
      return next;
    });
  };

  const saveCaption = async (img: ReviewImage) => {
    const key = imageKey(img);
    const caption = draftFor(img).trim();
    if (!caption) return;
    setBusyKeys(prev => new Set(prev).add(key));
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/indexes/${encodeURIComponent(batch.indexName || '')}/images/caption`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environment: batch.environment,
            edits: [{ docId: img.docId, imageIndex: img.imageIndex, imageUrl: img.imageUrl, docName: img.docName, caption }],
          }),
        }
      );
      const data = await response.json();
      const result = data.results?.[0];
      if (!result?.success) throw new Error(result?.error || data.error || 'Save failed');
      applyResult(img, caption);
    } catch (err: any) {
      setImagesError(`Failed to save caption for ${img.docName}: ${err.message}`);
    } finally {
      setBusyKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const recaptionOne = async (img: ReviewImage) => {
    const key = imageKey(img);
    setBusyKeys(prev => new Set(prev).add(key));
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/indexes/${encodeURIComponent(batch.indexName || '')}/images/recaption`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environment: batch.environment,
            items: [{ docId: img.docId, imageIndex: img.imageIndex, imageUrl: img.imageUrl, docName: img.docName }],
            guidance,
          }),
        }
      );
      const data = await response.json();
      const result = data.results?.[0];
      if (!result?.success) throw new Error(result?.error || data.error || 'Re-caption failed');
      applyResult(img, result.caption);
    } catch (err: any) {
      setImagesError(`Failed to re-caption ${img.docName}: ${err.message}`);
    } finally {
      setBusyKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const recaptionSelected = async () => {
    const targets = images.filter(img => selected.has(imageKey(img)));
    if (targets.length === 0) return;
    setRecaptioning(true);
    setImagesError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/indexes/${encodeURIComponent(batch.indexName || '')}/images/recaption`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environment: batch.environment,
            items: targets.map(img => ({
              docId: img.docId, imageIndex: img.imageIndex, imageUrl: img.imageUrl, docName: img.docName,
            })),
            guidance,
          }),
        }
      );
      const data = await response.json();
      const results: Array<{ docId: string; imageIndex: number; success: boolean; caption?: string; error?: string }> = data.results || [];
      const failures: string[] = [];
      setImages(prev => prev.map(img => {
        const result = results.find(r => r.docId === img.docId && r.imageIndex === img.imageIndex);
        if (!result) return img;
        if (result.success && result.caption) return { ...img, caption: result.caption };
        failures.push(`${img.docName} (page ${img.pageNumber}): ${result.error}`);
        return img;
      }));
      setDrafts({});
      setSelected(new Set());
      if (failures.length > 0) {
        setImagesError(`${failures.length} image(s) failed to re-caption: ${failures.join('; ')}`);
      }
    } catch (err: any) {
      setImagesError(err.message || 'Bulk re-caption failed');
    } finally {
      setRecaptioning(false);
    }
  };

  const saveVisibility = async (edits: Array<{ docId: string; imageIndex: number; hidden: boolean }>) => {
    const response = await authFetch(
      `${API_BASE}/admin/indexing/indexes/${encodeURIComponent(batch.indexName || '')}/images/visibility`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: batch.environment, edits }),
      }
    );
    const data = await response.json();
    const results: Array<{ docId: string; imageIndex: number; success: boolean; hidden?: boolean; error?: string }> = data.results || [];
    const failures: string[] = [];
    setImages(prev => prev.map(img => {
      const result = results.find(r => r.docId === img.docId && r.imageIndex === img.imageIndex);
      if (!result) return img;
      if (result.success) return { ...img, hidden: !!result.hidden };
      failures.push(`${img.docName} (page ${img.pageNumber}): ${result.error}`);
      return img;
    }));
    if (failures.length > 0) {
      setImagesError(`${failures.length} image(s) failed to update: ${failures.join('; ')}`);
    }
  };

  const toggleHidden = async (img: ReviewImage) => {
    const key = imageKey(img);
    setBusyKeys(prev => new Set(prev).add(key));
    try {
      await saveVisibility([{ docId: img.docId, imageIndex: img.imageIndex, hidden: !img.hidden }]);
    } catch (err: any) {
      setImagesError(`Failed to update visibility for ${img.docName}: ${err.message}`);
    } finally {
      setBusyKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const suggestedCount = images.filter(img => img.suggestedHidden && !img.hidden).length;
  const hiddenCount = images.filter(img => img.hidden).length;

  const acceptSuggested = async () => {
    const targets = images.filter(img => img.suggestedHidden && !img.hidden);
    if (targets.length === 0) return;
    setRecaptioning(true);
    setImagesError(null);
    try {
      await saveVisibility(targets.map(img => ({ docId: img.docId, imageIndex: img.imageIndex, hidden: true })));
    } catch (err: any) {
      setImagesError(err.message || 'Failed to accept AI suggestions');
    } finally {
      setRecaptioning(false);
    }
  };

  const showAllImages = async () => {
    const targets = images.filter(img => img.hidden);
    if (targets.length === 0) return;
    setRecaptioning(true);
    setImagesError(null);
    try {
      await saveVisibility(targets.map(img => ({ docId: img.docId, imageIndex: img.imageIndex, hidden: false })));
    } catch (err: any) {
      setImagesError(err.message || 'Failed to show all images');
    } finally {
      setRecaptioning(false);
    }
  };

  const sendQuery = async (query: string) => {
    if (!query.trim() || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: query }]);
    setInput('');
    setSending(true);

    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/test-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: batch.agentId,
          indexName: batch.indexName,
          query,
          environment: batch.environment,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          citations: data.citations,
          latencyMs: data.latencyMs,
          agentName: data.agentName,
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Error: ${data.error || 'Query failed'}`,
        }]);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message}`,
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuery(input);
    }
  };

  const color = getAgentColor(batch.agentId);
  const samples = SAMPLE_QUERIES[batch.agentId] || ['Test query for this agent'];
  const changes = batch.changes || { added: [], modified: [], removed: [] };
  const totalChanges = (changes.added?.length || 0) + (changes.modified?.length || 0) + (changes.removed?.length || 0);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 640,
      background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 1000, display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px 0', borderBottom: '1px solid var(--idx-border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ paddingBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Bot size={20} style={{ color }} />
            <span style={{ fontWeight: 600, fontSize: 16 }}>Test: {batch.agentDisplayName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--idx-text-muted)' }}>
            <Database size={12} />
            <code style={{ background: 'var(--idx-bg-tertiary)', padding: '1px 4px', borderRadius: 3 }}>
              {batch.indexName}
            </code>
            <span>{batch.environment.toUpperCase()}</span>
            <span>{totalChanges} change{totalChanges !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <X size={20} style={{ color: 'var(--idx-text-muted)' }} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, padding: '0 20px', borderBottom: '1px solid var(--idx-border)', background: 'var(--idx-bg-primary)' }}>
        {([
          { id: 'chat' as const, label: 'Chat Test', icon: MessageSquare },
          { id: 'images' as const, label: `Review Images${images.length ? ` (${images.length})` : ''}`, icon: ImageIcon },
        ]).map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: active ? '2px solid #10b981' : '2px solid transparent',
                color: active ? 'var(--idx-text-primary)' : 'var(--idx-text-muted)',
                fontSize: 13, fontWeight: active ? 600 : 500, marginBottom: -1,
              }}
            >
              <Icon size={13} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'chat' ? (
        <>
      {/* Chat area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--idx-text-muted)', padding: '24px 0' }}>
            <MessageSquare size={32} style={{ margin: '0 auto 12px', color: 'var(--idx-text-faint)' }} />
            <p style={{ fontWeight: 500, marginBottom: 4 }}>
              Test the new index before promoting
            </p>
            <p style={{ fontSize: 12, color: 'var(--idx-text-faint)', marginBottom: 16 }}>
              Queries are routed through the live agent using this index
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 320, margin: '0 auto' }}>
              {samples.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendQuery(q)}
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--idx-border)',
                    cursor: 'pointer', background: 'var(--idx-bg-secondary)', textAlign: 'left',
                    fontSize: 13, color: 'var(--idx-text-secondary)',
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            marginBottom: 16, display: 'flex', gap: 10,
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: msg.role === 'user' ? '#10b981' : color,
            }}>
              {msg.role === 'user' ? <User size={14} style={{ color: '#fff' }} /> : <Bot size={14} style={{ color: '#fff' }} />}
            </div>
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: 12,
              background: msg.role === 'user' ? '#eff6ff' : 'var(--idx-bg-secondary)',
              border: msg.role === 'user' ? '1px solid #bfdbfe' : '1px solid var(--idx-border)',
            }}>
              {msg.agentName && (
                <div style={{ fontSize: 10, fontWeight: 600, color, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {msg.agentName}
                </div>
              )}
              {msg.role === 'assistant' ? (
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {/* Rendered through the same markdown/image pipeline as the live chat
                      (StreamingText) so `![caption](url)` citations actually render as
                      images here — not just as raw markdown text. See AA-220. */}
                  <StreamingText content={msg.content} isComplete imageMaxWidth="240px" />
                </div>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </div>
              )}
              {msg.citations && msg.citations.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px solid var(--idx-border)', paddingTop: 6 }}>
                  <button
                    onClick={() => toggleCitations(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
                      cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 500, color: 'var(--idx-text-muted)',
                    }}
                  >
                    {expandedCitations.has(i) ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    Sources ({msg.citations.length})
                  </button>
                  {!expandedCitations.has(i) && (
                    <div style={{ marginTop: 4 }}>
                      {msg.citations.map((c, j) => (
                        <div key={j} style={{ fontSize: 11, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                          <FileText size={10} />
                          {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.title}</a> : c.title}
                        </div>
                      ))}
                    </div>
                  )}
                  {expandedCitations.has(i) && (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {msg.citations.map((c, j) => (
                        <div key={j} style={{
                          fontSize: 11, background: 'var(--idx-bg-tertiary)', borderRadius: 8, padding: '8px 10px',
                        }}>
                          <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, fontWeight: 500 }}>
                            <FileText size={10} />
                            {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.title}</a> : c.title}
                          </div>
                          {c.content && (
                            <div style={{ color: 'var(--idx-text-secondary)' }}>
                              <StreamingText content={c.content} isComplete imageMaxWidth="200px" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {msg.latencyMs && (
                <div style={{ fontSize: 10, color: 'var(--idx-text-faint)', marginTop: 4 }}>{msg.latencyMs}ms</div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', background: color,
            }}>
              <Bot size={14} style={{ color: '#fff' }} />
            </div>
            <div style={{ padding: '12px 16px', background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)', borderRadius: 12 }}>
              <Loader2 size={16} className="spinning" style={{ color: 'var(--idx-text-muted)' }} />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--idx-border)' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a test question..."
            disabled={sending}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--idx-border-strong)',
              fontSize: 14, outline: 'none', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
            }}
          />
          <button
            onClick={() => sendQuery(input)}
            disabled={sending || !input.trim()}
            style={{
              padding: '10px 14px', borderRadius: 8, border: 'none',
              cursor: 'pointer', background: '#10b981', color: '#fff',
            }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
        </>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {imagesLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--idx-text-muted)', fontSize: 13, padding: '24px 0', justifyContent: 'center' }}>
              <Loader2 size={16} className="spinning" /> Scanning index for images...
            </div>
          )}

          {imagesError && (
            <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
              {imagesError}
            </div>
          )}

          {!imagesLoading && imagesLoaded && images.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--idx-text-muted)', padding: '24px 0' }}>
              <ImageIcon size={32} style={{ margin: '0 auto 12px', color: 'var(--idx-text-faint)' }} />
              <p style={{ fontWeight: 500, marginBottom: 4 }}>No images found in this index</p>
              <p style={{ fontSize: 12, color: 'var(--idx-text-faint)' }}>
                Either this agent's image_mode is "skip", or the source documents have no images.
              </p>
            </div>
          )}

          {images.length > 0 && (
            <>
              {/* Bulk controls */}
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px',
                background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)', borderRadius: 8,
              }}>
                {/* Visibility summary + bulk hide/show actions (AA-220 follow-up).
                    Hidden images never reach the model — see kb_concierge.py's
                    _chunk_image_pairs — so this is a token-cost lever too. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--idx-text-muted)' }}>
                    <EyeOff size={12} />
                    {hiddenCount} hidden of {images.length}
                    {suggestedCount > 0 && (
                      <span style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Sparkles size={11} /> {suggestedCount} AI-flagged, unreviewed
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={acceptSuggested}
                      disabled={suggestedCount === 0 || recaptioning}
                      title="Hide every image the Vision captioner flagged as a banner, decorative element, or generic UI icon"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
                        border: '1px solid var(--idx-border-strong)', cursor: (suggestedCount === 0 || recaptioning) ? 'default' : 'pointer',
                        background: suggestedCount === 0 ? 'var(--idx-bg-tertiary)' : '#fffbeb', color: suggestedCount === 0 ? 'var(--idx-text-faint)' : '#d97706',
                        fontSize: 11, fontWeight: 500,
                      }}
                    >
                      <Sparkles size={11} /> Accept AI suggestions ({suggestedCount})
                    </button>
                    <button
                      onClick={showAllImages}
                      disabled={hiddenCount === 0 || recaptioning}
                      title="Un-hide every image in this index"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
                        border: '1px solid var(--idx-border-strong)', cursor: (hiddenCount === 0 || recaptioning) ? 'default' : 'pointer',
                        background: 'var(--idx-bg-tertiary)', color: hiddenCount === 0 ? 'var(--idx-text-faint)' : 'var(--idx-text-secondary)',
                        fontSize: 11, fontWeight: 500,
                      }}
                    >
                      <Eye size={11} /> Show all images
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <button
                    onClick={toggleSelectAll}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--idx-text-secondary)', padding: 0 }}
                  >
                    <Check size={12} /> {selected.size === images.length ? 'Deselect all' : `Select all (${images.length})`}
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--idx-text-muted)' }}>{selected.size} selected</span>
                </div>
                <textarea
                  value={guidance}
                  onChange={e => setGuidance(e.target.value)}
                  placeholder='Optional guidance for re-captioning, e.g. "focus on the pressure gauge reading" or "these are all fire extinguisher diagrams — describe the specific part shown"'
                  rows={2}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 12, resize: 'vertical',
                    border: '1px solid var(--idx-border-strong)', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={recaptionSelected}
                  disabled={selected.size === 0 || recaptioning}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 6, border: 'none', cursor: selected.size === 0 ? 'default' : 'pointer',
                    background: selected.size === 0 ? 'var(--idx-bg-tertiary)' : '#7c3aed', color: selected.size === 0 ? 'var(--idx-text-faint)' : '#fff',
                    fontSize: 12, fontWeight: 500,
                  }}
                >
                  {recaptioning ? <Loader2 size={13} className="spinning" /> : <Wand2 size={13} />}
                  Re-caption selected ({selected.size}) with Vision
                </button>
              </div>

              {/* Image list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {images.map((img) => {
                  const key = imageKey(img);
                  const busy = busyKeys.has(key);
                  const dirty = isDirty(img);
                  return (
                    <div key={key} style={{
                      display: 'flex', gap: 10, padding: 10, borderRadius: 8,
                      background: 'var(--idx-bg-secondary)',
                      border: selected.has(key) ? '1px solid #7c3aed' : '1px solid var(--idx-border)',
                      opacity: img.hidden ? 0.55 : 1,
                    }}>
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggleSelected(key)}
                        style={{ marginTop: 4, flexShrink: 0, cursor: 'pointer' }}
                      />
                      <img
                        src={img.imageUrl}
                        alt={img.caption || 'index image'}
                        style={{ width: 90, height: 90, objectFit: 'contain', borderRadius: 6, background: 'var(--idx-bg-tertiary)', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', minWidth: 0 }}>
                            <FileText size={10} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {img.docName}{img.pageNumber ? ` · p.${img.pageNumber}` : ''}{img.sectionTitle ? ` · ${img.sectionTitle}` : ''}
                            </span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            {img.suggestedHidden && (
                              <span title="Vision flagged this as a banner, decorative element, or generic UI icon" style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#d97706' }}>
                                <Sparkles size={10} /> AI suggested
                              </span>
                            )}
                            <button
                              onClick={() => toggleHidden(img)}
                              disabled={busy}
                              title={img.hidden ? 'Hidden — never surfaced to the agent. Click to show.' : 'Visible to the agent. Click to hide.'}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 10,
                                border: 'none', cursor: busy ? 'default' : 'pointer',
                                background: img.hidden ? '#fee2e2' : 'var(--idx-bg-tertiary)',
                                color: img.hidden ? '#dc2626' : 'var(--idx-text-secondary)',
                                fontSize: 10, fontWeight: 600,
                              }}
                            >
                              {img.hidden ? <EyeOff size={10} /> : <Eye size={10} />} {img.hidden ? 'Hidden' : 'Visible'}
                            </button>
                          </span>
                        </div>
                        <textarea
                          value={draftFor(img)}
                          onChange={e => setDrafts(prev => ({ ...prev, [key]: e.target.value }))}
                          rows={2}
                          disabled={busy}
                          style={{
                            width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 12, resize: 'vertical',
                            border: `1px solid ${dirty ? '#f59e0b' : 'var(--idx-border-strong)'}`,
                            background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)', boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => saveCaption(img)}
                            disabled={!dirty || busy}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
                              border: '1px solid var(--idx-border-strong)', cursor: (!dirty || busy) ? 'default' : 'pointer',
                              background: dirty ? '#10b981' : 'var(--idx-bg-tertiary)', color: dirty ? '#fff' : 'var(--idx-text-faint)',
                              fontSize: 11, fontWeight: 500,
                            }}
                          >
                            {busy ? <Loader2 size={11} className="spinning" /> : <Pencil size={11} />} Save edit
                          </button>
                          <button
                            onClick={() => recaptionOne(img)}
                            disabled={busy}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
                              border: '1px solid var(--idx-border-strong)', cursor: busy ? 'default' : 'pointer',
                              background: 'var(--idx-bg-tertiary)', color: 'var(--idx-text-secondary)',
                              fontSize: 11, fontWeight: 500,
                            }}
                          >
                            {busy ? <Loader2 size={11} className="spinning" /> : <RefreshCw size={11} />} Re-caption
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Action bar */}
      <div style={{
        padding: '12px 20px', borderTop: '1px solid var(--idx-border)', background: 'var(--idx-bg-secondary)',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <button
          onClick={() => onReject(batch)}
          style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid #fecaca',
            cursor: 'pointer', background: 'var(--idx-bg-primary)', color: '#dc2626',
            fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Trash2 size={14} /> Reject & Delete Index
        </button>
        <button
          onClick={() => onPromote(batch)}
          style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            cursor: 'pointer', background: '#16a34a', color: '#fff',
            fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <ArrowUpCircle size={14} /> Promote to Active
        </button>
      </div>
    </div>
  );
};

export default IndexTestPanel;
